import { useMemo } from 'react'
import { usePortfolioHouseVisits } from '../../StateManagment/PortfolioHouseVisitsContext'
import {
    aggregateAdressesByProject,
    aggregateVisitsByProject,
    computeReachMetrics,
} from '../../helpers/housevisitsPortfolio/aggregateHouseVisits'
import StaticCardLabel from './StaticCardLabel'
import KpiValue from './KpiValue'
import DashboardCardOptions from './DashboardCardOptions'
import CardHeaderInfoPopover from '../common/CardHeaderInfoPopover'
import ProjectContributionBreakdown from './housevisitsPortfolio/ProjectContributionBreakdown'

// AI-CONTEXT: See docs/patterns.md#dashboard-static-card

const REACH_COLUMNS = [
    { key: 'completed', label: 'Voltooid' },
    { key: 'visits', label: 'Bezoeken' },
]

/**
 * Stat card in de KPI-rij van een portfolio: hoeveel huishoudens geholpen zijn
 * (status 'Afgerond'), met het aantal bezoeken eronder.
 *
 * @param {object} props
 * @param {object} props.item - The DashboardItems doc backing this static card
 */
const DashboardPortfolioHouseVisitsReachedCard = ({ item }) => {
    const { projects, adresses, tasks, loading } = usePortfolioHouseVisits()

    const adressRows = useMemo(
        () => aggregateAdressesByProject(projects, adresses),
        [projects, adresses]
    )
    const visitRows = useMemo(
        () => aggregateVisitsByProject(projects, tasks),
        [projects, tasks]
    )
    // De ruwe adressen gaan mee zodat een woning die door twee organisaties
    // wordt geholpen één keer telt. Zonder die derde parameter telt de kaart de
    // projecttotalen op — gemeten 2026-09-03: 4 getoond waar er 3 waren.
    // AI-CONTEXT: See docs/test-runs/2026-09-03-energiearmoede-arnhem-scenarios.md
    const metrics = useMemo(
        () => computeReachMetrics(adressRows, visitRows, adresses),
        [adressRows, visitRows, adresses]
    )

    const breakdown = useMemo(() => {
        const visitMap = new Map(visitRows.map((r) => [r.CompagnyID, r]))
        return adressRows.map((row) => ({
            CompagnyID: row.CompagnyID,
            name: row.name,
            byColumn: {
                completed: row.byStatus?.completed || 0,
                visits: visitMap.get(row.CompagnyID)?.visitCount || 0,
            },
        }))
    }, [adressRows, visitRows])

    // AI-CONTEXT: See docs/pitfalls.md#ui-aggregate-collapses-nothing-into-zero
    const isEmpty = !loading && projects.length === 0
    const avg = String(metrics.avgVisitsPerHousehold).replace('.', ',')

    return (
        <div className='participation-value-card'>
            <StaticCardLabel
                title='Bereikte huishoudens'
                pill={{ variant: 'housevisits', label: 'Portfolio' }}
            />
            {!isEmpty && !loading && (
                <CardHeaderInfoPopover
                    content={<ProjectContributionBreakdown items={breakdown} columns={REACH_COLUMNS} />}
                    label='Bijdrage per project'
                />
            )}
            <KpiValue loading={loading} value={isEmpty ? null : metrics.totalCompleted} />
            {!loading && (
                <p className='participation-value-note'>
                    {isEmpty
                        ? 'Er hangen nog geen projecten met huisbezoeken onder dit portfolio.'
                        : `${metrics.totalVisits} bezoeken · gem. ${avg} per huishouden`}
                </p>
            )}
            {/* Zonder deze regel leest het getal als 'wij hielpen er zoveel'
                terwijl een deel samen met een andere organisatie ging. Alleen
                tonen als het echt voorkomt — bij de meeste portfolio's is het 0. */}
            {!loading && metrics.sharedHouseholds > 0 && (
                <p className='participation-value-note'>
                    {metrics.sharedHouseholds === 1
                        ? '1 huishouden werd samen met een andere organisatie geholpen'
                        : `${metrics.sharedHouseholds} huishoudens werden samen met een andere organisatie geholpen`}
                </p>
            )}
            <DashboardCardOptions item={item} />
        </div>
    )
}

export default DashboardPortfolioHouseVisitsReachedCard
